import { useState, useMemo, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { getGuideCategoryToBoats, getGuideBoatsListing } from "store/boats"
import {
  fetchAssignedEquipments,
  assignEquipmentToBooking,
  unassignEquipmentFromBooking,
  selectFetchAssignedEquipmentsLoading,
  selectAssignEquipmentLoading,
  selectUnassignEquipmentLoading
} from "store/equipmentAssignments"

const useEquipmentManagement = (bookingId) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const [assignedEquipment, setAssignedEquipment] = useState([])
  const [showAddEquipment, setShowAddEquipment] = useState(false)
  const [selectedCategories, setSelectedCategories] = useState([])

  // Fetch categories and equipment from Redux
  const categoryListing = useSelector((state) => state.boats.category_to_boats.items)
  const equipmentListing = useSelector((state) => state.boats.items)
  const fetchLoading = useSelector(selectFetchAssignedEquipmentsLoading)
  const assignLoading = useSelector(selectAssignEquipmentLoading)
  const unassignLoading = useSelector(selectUnassignEquipmentLoading)

  useEffect(() => {
    dispatch(getGuideCategoryToBoats())
    dispatch(getGuideBoatsListing(null, { list_for: "assign_equipment", booking_id: bookingId }))
  }, [dispatch, bookingId])

  const refreshAssignedEquipment = () => {
    if (!bookingId) return
    dispatch(fetchAssignedEquipments(bookingId)).then((data) => {
      const mapped = (Array.isArray(data) ? data : []).map((item) => ({
        id: item.id,
        equipmentId: item.equipment_id,
        name: item.equipment_name,
        assetNumber: item.asset_number,
        assetId: item.asset_id,
        category: item.category
      }))
      setAssignedEquipment(mapped)
    })
  }

  useEffect(() => {
    refreshAssignedEquipment()
  }, [bookingId, dispatch])

  // Transform category data to options format
  const categoryOptions = useMemo(() => {
    const options = categoryListing.map((cat) => ({
      label: cat?.name,
      value: String(cat?.id)
    }))
    return [{ label: t("All") || "All", value: "all" }, ...options]
  }, [categoryListing, t])

  // Transform equipment data to table format
  const availableEquipment = useMemo(() => {
    return equipmentListing.map((equipment) => ({
      id: equipment.id,
      name: equipment.equipment_title || equipment.name || "",
      assetNumbers: (equipment.boat_assets || []).map((asset) => asset.asset_number),
      category: typeof equipment.category === "string" ? equipment.category : equipment.category?.name || ""
    }))
  }, [equipmentListing])

  // Create a map of category ID to name for filtering
  const categoryIdToNameMap = useMemo(() => {
    return categoryListing.reduce((acc, cat) => {
      if (cat?.id !== undefined && cat?.name) {
        acc[String(cat.id)] = cat.name
      }
      return acc
    }, {})
  }, [categoryListing])

  const handleUnassign = (equipmentId, assetId) => {
    dispatch(unassignEquipmentFromBooking(bookingId, equipmentId, assetId)).then(() => {
      setAssignedEquipment((prev) => prev.filter((eq) => !(eq.equipmentId === equipmentId && eq.assetId === assetId)))
    })
  }

  const handleAssign = (equipment) => {
    dispatch(assignEquipmentToBooking(bookingId, equipment.id)).then(() => {
      refreshAssignedEquipment()
    })
  }

  const handleCategoryFilter = (values) => {
    setSelectedCategories(values)
  }

  const toggleAddEquipment = () => {
    setShowAddEquipment(!showAddEquipment)
  }

  const closeAddEquipment = () => {
    setShowAddEquipment(false)
  }

  const filteredAvailableEquipment = useMemo(() => {
    // Exclude equipment that already has assets assigned
    const assignedEquipmentIds = assignedEquipment.map((eq) => eq.equipmentId)
    let filtered = availableEquipment.filter((eq) => !assignedEquipmentIds.includes(eq.id))

    // Filter by selected categories
    if (selectedCategories.length > 0 && !selectedCategories.includes("all")) {
      const selectedCategoryNames = selectedCategories
        .filter((id) => id !== "all")
        .map((id) => categoryIdToNameMap[id])
        .filter(Boolean)

      filtered = filtered.filter((eq) => selectedCategoryNames.includes(eq.category))
    }

    return filtered
  }, [availableEquipment, assignedEquipment, selectedCategories, categoryIdToNameMap])

  return {
    assignedEquipment,
    availableEquipment,
    filteredAvailableEquipment,
    showAddEquipment,
    selectedCategories,
    categoryOptions,
    handleUnassign,
    handleAssign,
    handleCategoryFilter,
    toggleAddEquipment,
    closeAddEquipment,
    setSelectedCategories,
    fetchLoading,
    assignLoading,
    unassignLoading
  }
}

export default useEquipmentManagement
